import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X, Mic, MicOff, Camera, Upload, Type, Send, Sparkles,
  FileText, Loader2, Edit3, ChevronDown,
} from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { HOME_OS_MODULES } from "../data/homeOsModuleConfig";
import { SUPPLY_CATEGORIES } from "../data/suppliesData";
import {
  routeQuickCaptureInput, MODULE_OPTIONS, ACTION_OPTIONS,
} from "../utils/quickCaptureRouter";

/* ════════════════════════════════════════════════════════════════════════
   QUICK CAPTURE MODAL — voice, photo, file, or typed instruction.
   Now powered by the quickCaptureRouter so ARA shows a confidence-aware
   "this belongs in…" panel that the user can override before saving.
   ════════════════════════════════════════════════════════════════════════ */

async function fileToDataUrl(file, max = 800) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (!file.type.startsWith("image/")) {
        resolve({ kind: "file", name: file.name, mime: file.type, size: file.size, dataUrl: null });
        return;
      }
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ratio = Math.min(1, max / Math.max(img.width, img.height));
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ kind: "image", name: file.name, mime: "image/jpeg", size: file.size, dataUrl: canvas.toDataURL("image/jpeg", 0.82) });
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const CONFIDENCE_STYLE = {
  high:   { label: "High confidence",   chip: "bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-transparent dark:text-[color:var(--ara-ink-muted)]" },
  medium: { label: "Medium confidence", chip: "bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-transparent dark:text-[color:var(--ara-ink-muted)]" },
  low:    { label: "Low confidence",    chip: "bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-transparent dark:text-[color:var(--ara-ink-muted)]" },
};

export function QuickCaptureModal({ open, onClose }) {
  const {
    members,
    addTask, addShoppingItem, addSupplyItem, setSupplyStatus, suppliesItems,
    addMoodCheckin, addQuickCapture,
  } = useHomeOSState();

  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [listening, setListening] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* User overrides for the suggestion. null = follow the auto routing. */
  const [override, setOverride] = useState({
    module: null, action: null, person: null, date: null, supplyCategory: null,
    itemName: null, edit: false,
  });

  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setText("");
      setAttachments([]);
      setOverride({ module: null, action: null, person: null, date: null, supplyCategory: null, itemName: null, edit: false });
      stopListening();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* Auto-route based on current text + attachments. */
  const routed = useMemo(() => routeQuickCaptureInput(text, attachments), [text, attachments]);

  /* Effective suggestion = routed values with optional user overrides. */
  const effective = useMemo(() => ({
    module: override.module || routed.module,
    action: override.action || routed.action,
    person: override.person ?? routed.extracted.person,
    date:   override.date   ?? routed.extracted.date,
    time:   routed.extracted.time,
    item:   override.itemName ?? routed.extracted.item,
    supplyCategory: override.supplyCategory ?? routed.extracted.supplyCategory,
  }), [routed, override]);

  const moduleConfig = HOME_OS_MODULES[effective.module] || HOME_OS_MODULES.today;
  const confidenceStyle = CONFIDENCE_STYLE[routed.confidence] || CONFIDENCE_STYLE.medium;

  /* ── Voice ── */
  const startListening = () => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      toast.error("Voice input isn't supported in this browser. Try Chrome.");
      return;
    }
    try {
      const recognition = new SR();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = "en-NZ";
      recognition.maxAlternatives = 1;
      recognition.onstart = () => setListening(true);
      recognition.onresult = (event) => {
        let finalText = "";
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalText += t;
          else interim += t;
        }
        setText(prev => {
          const base = prev.replace(/\s*…$/, "");
          if (finalText) return (base + " " + finalText).trim();
          return (base + " " + interim).trim() + "…";
        });
      };
      recognition.onerror = (event) => {
        setListening(false);
        if (event.error === "not-allowed") toast.error("Mic blocked. Allow microphone access.");
        else if (event.error !== "aborted") toast.error(`Voice error: ${event.error}`);
      };
      recognition.onend = () => { setListening(false); setText(prev => prev.replace(/\s*…$/, "")); };
      recognitionRef.current = recognition;
      recognition.start();
    } catch {
      toast.error("Couldn't start voice. Try again.");
      setListening(false);
    }
  };
  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    setListening(false);
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const processed = await Promise.all(files.map(f => fileToDataUrl(f).catch(() => null)));
    setAttachments(prev => [...prev, ...processed.filter(Boolean)]);
  };
  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  /* ── Save according to effective module + action ── */
  const submit = () => {
    const cleanText = text.replace(/\s*…$/, "").trim();
    if (!cleanText && attachments.length === 0) {
      toast.error("Add a voice note, photo, file, or type something first.");
      return;
    }
    setSubmitting(true);

    const firstImage = attachments.find(a => a.kind === "image");
    const firstFile = attachments[0];
    const titleSeed = cleanText
      ? cleanText.split(/[.!?]/)[0].slice(0, 80)
      : firstImage ? "Captured photo" : "Quick capture";

    const relatedIds = {};
    const ext = effective;

    /* Decide what to write based on effective.action. */
    switch (ext.action) {
      case "add-supply-low": {
        const itemName = ext.item || titleSeed;
        // Check if a matching supply already exists → mark low instead of duplicating
        const existing = (suppliesItems || []).find(s => s.name.toLowerCase().trim() === itemName.toLowerCase().trim());
        if (existing) {
          setSupplyStatus(existing.id, existing.status === "Out" ? "Out" : "Low");
          relatedIds.supplyId = existing.id;
        } else {
          relatedIds.supplyId = addSupplyItem({
            name: itemName,
            category: ext.supplyCategory || "Household",
            status: "Low",
            quantity: 1,
            unit: "",
            autoShop: true,
            notes: cleanText ? `Captured: ${cleanText}` : "",
          });
        }
        relatedIds.shoppingId = addShoppingItem({
          item: itemName,
          category: ext.supplyCategory === "Pantry" ? "Pantry" : "Household",
          quantity: "",
          sourceSupplyId: relatedIds.supplyId,
        });
        break;
      }
      case "add-shopping": {
        relatedIds.shoppingId = addShoppingItem({
          item: ext.item || titleSeed,
          category: ext.supplyCategory === "Pantry" ? "Pantry" : "Other",
          quantity: "",
        });
        break;
      }
      case "add-meal-plan": {
        relatedIds.taskId = addTask({
          title: titleSeed,
          description: cleanText,
          category: "Meals",
          assignedTo: ext.person,
          dueDate: ext.date,
          status: "Upcoming",
          source: "quick capture",
        });
        break;
      }
      case "create-reminder": {
        relatedIds.taskId = addTask({
          title: titleSeed,
          description: cleanText,
          category: ext.module === "money" ? "Money" :
                    ext.module === "health" ? "Health" :
                    ext.module === "calendar" ? "Calendar" : "Calendar",
          assignedTo: ext.person,
          dueDate: ext.date,
          dueTime: ext.time,
          status: "Needs action",
          approvalRequired: ext.module === "money",
          source: "quick capture",
          photoUrl: firstImage?.dataUrl || null,
          attachments: attachments.map(a => ({ name: a.name, kind: a.kind, mime: a.mime, dataUrl: a.dataUrl })),
        });
        break;
      }
      case "add-document": {
        relatedIds.captureId = addQuickCapture({
          module: "documents",
          action: "add-document",
          title: titleSeed,
          text: cleanText,
          attachments: attachments.map(a => ({ name: a.name, kind: a.kind, mime: a.mime, dataUrl: a.dataUrl })),
          extracted: ext,
        });
        // Also add a Documents task so it shows on Today
        relatedIds.taskId = addTask({
          title: titleSeed,
          description: cleanText,
          category: "Documents",
          assignedTo: ext.person,
          dueDate: ext.date,
          status: "Needs action",
          source: "quick capture",
          relatedFile: firstFile?.name || null,
          photoUrl: firstImage?.dataUrl || null,
          attachments: attachments.map(a => ({ name: a.name, kind: a.kind, mime: a.mime, dataUrl: a.dataUrl })),
        });
        break;
      }
      case "add-mood": {
        const matchPerson = (members || []).find(m => (m.name || "").toLowerCase() === (ext.person || "").toLowerCase());
        relatedIds.moodId = addMoodCheckin({
          memberId: matchPerson?.id || null,
          moodId: "low",
          moodLabel: "Sad",
          zone: "blue",
          energy: "Low",
          note: cleanText,
          source: "quick-capture",
        });
        break;
      }
      case "add-task":
      default: {
        relatedIds.taskId = addTask({
          title: titleSeed,
          description: cleanText,
          category: ext.module === "cleaning" ? "Cleaning" :
                    ext.module === "supplies" ? "Supplies" : "Chores",
          assignedTo: ext.person,
          dueDate: ext.date,
          status: "Needs action",
          source: "quick capture",
          photoUrl: firstImage?.dataUrl || null,
          attachments: attachments.map(a => ({ name: a.name, kind: a.kind, mime: a.mime, dataUrl: a.dataUrl })),
        });
        break;
      }
    }

    // Always log the capture itself (lightweight history for documents page later)
    if (!relatedIds.captureId) {
      addQuickCapture({
        module: ext.module,
        action: ext.action,
        title: titleSeed,
        text: cleanText,
        attachments: attachments.map(a => ({ name: a.name, kind: a.kind, mime: a.mime, dataUrl: a.dataUrl })),
        extracted: ext,
        relatedIds,
      });
    }

    toast.success(`Saved to ${moduleConfig.label}${ext.date ? ` for ${ext.date}` : ""}.`);
    setSubmitting(false);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="quick-capture-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-none font-home animate-fadeUp">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl    flex items-center justify-center shadow-sm">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-50">Quick Capture</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Voice, photo, file, or type — ARA sorts it.</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-400 hover:text-gray-600" data-testid="quick-capture-close">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">

          {/* Voice button */}
          <button
            type="button"
            onClick={listening ? stopListening : startListening}
            className={`w-full rounded-2xl border-2 px-5 py-4 flex items-center gap-4 transition-all duration-200 ${
              listening
                ? "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-rose-700 animate-pulse"
                : "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-violet-800 hover:border-violet-400"
            }`}
            data-testid="quick-capture-voice-btn"
          >
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
              listening ? "bg-transparent text-white" : "bg-transparent text-white"
            }`}>
              {listening ? <MicOff className="w-6 h-6" strokeWidth={1.5} /> : <Mic className="w-6 h-6" strokeWidth={1.5} />}
            </div>
            <div className="flex-1 text-left">
              <p className={`text-sm font-extrabold ${listening ? "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" : "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]"}`}>
                {listening ? "Listening… tap to stop" : "Hold to speak"}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium leading-tight">
                "Milk is low" · "Pay power bill Friday" · "Book dentist Tuesday"
              </p>
            </div>
          </button>

          {/* Capture row */}
          <div className="grid grid-cols-3 gap-2">
            <button type="button" onClick={() => cameraInputRef.current?.click()} className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-[color:var(--ara-line-soft)] px-3 py-3 flex flex-col items-center gap-1.5 text-gray-700 dark:text-gray-200" data-testid="quick-capture-camera-btn">
              <Camera className="w-5 h-5 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />
              <span className="text-[11px] font-extrabold">Take photo</span>
            </button>
            <button type="button" onClick={() => fileInputRef.current?.click()} className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-[color:var(--ara-line-soft)] px-3 py-3 flex flex-col items-center gap-1.5 text-gray-700 dark:text-gray-200" data-testid="quick-capture-file-btn">
              <Upload className="w-5 h-5 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />
              <span className="text-[11px] font-extrabold">Upload file</span>
            </button>
            <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-3 flex flex-col items-center gap-1.5 text-gray-700 dark:text-gray-200">
              <Type className="w-5 h-5 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />
              <span className="text-[11px] font-extrabold">Type below</span>
            </div>
          </div>

          {/* Text area */}
          <div>
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Instruction</span>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Try: 'Milk is low' · 'Pay power bill Friday' · 'Book dentist Tuesday 3pm' · 'Clean bathroom tomorrow'"
              rows={3}
              className="mt-1 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-medium text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
              data-testid="quick-capture-textarea"
            />
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Attached</span>
              <div className="mt-1 flex flex-wrap gap-2">
                {attachments.map((a, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 flex items-center justify-center" data-testid={`quick-capture-attachment-${idx}`}>
                    {a.kind === "image" && a.dataUrl ? (
                      <img src={a.dataUrl} alt={a.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center gap-1 px-1 text-center">
                        <FileText className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                        <span className="text-[9px] font-bold text-gray-500 truncate w-full">{a.name}</span>
                      </div>
                    )}
                    <button type="button" onClick={() => removeAttachment(idx)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-transparent" data-testid={`quick-capture-remove-${idx}`} aria-label="Remove attachment">
                      <X className="w-3 h-3" strokeWidth={3} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ARA suggestion panel */}
          {(text.trim() || attachments.length > 0) && (
            <div className={`rounded-2xl border-2 p-4 ${moduleConfig.color.tile}`} data-testid="quick-capture-suggestion">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`w-2 h-2 rounded-full ${moduleConfig.color.dot}`} />
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">ARA thinks this belongs in</p>
                <span className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold ${confidenceStyle.chip}`} data-testid="quick-capture-confidence">
                  {confidenceStyle.label}
                </span>
              </div>

              <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className={`text-xl font-extrabold leading-tight ${moduleConfig.color.textAccent}`} data-testid="quick-capture-suggestion-module">
                    {moduleConfig.label}
                  </p>
                  <p className="text-xs font-bold text-gray-600 dark:text-gray-300">
                    {ACTION_OPTIONS.find(a => a.id === effective.action)?.label || "Save"}
                  </p>
                </div>
                <button
                  onClick={() => setOverride(o => ({ ...o, edit: !o.edit }))}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs font-extrabold text-gray-700 dark:text-gray-200"
                  data-testid="quick-capture-edit-suggestion"
                >
                  <Edit3 className="w-3 h-3" strokeWidth={1.5} />
                  {override.edit ? "Done" : "Change"}
                </button>
              </div>

              {/* Extracted details preview */}
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="quick-capture-extracted">
                {effective.item && <Chip>Item: {effective.item}</Chip>}
                {effective.date && <Chip>Date: {effective.date}</Chip>}
                {effective.time && <Chip>Time: {effective.time}</Chip>}
                {effective.person && <Chip>Person: {effective.person}</Chip>}
                {effective.supplyCategory && effective.module === "supplies" && <Chip>{effective.supplyCategory}</Chip>}
              </div>

              <p className="mt-2 text-[11px] italic text-gray-500 dark:text-gray-400">{routed.reason}</p>

              {/* Inline editor */}
              {override.edit && (
                <div className="mt-3 grid grid-cols-2 gap-2 pt-3 border-t border-black/5 dark:border-white/5" data-testid="quick-capture-edit-panel">
                  <Select
                    label="Module"
                    value={effective.module}
                    onChange={(v) => setOverride(o => ({ ...o, module: v }))}
                    options={MODULE_OPTIONS}
                    testId="quick-capture-override-module"
                  />
                  <Select
                    label="Action"
                    value={effective.action}
                    onChange={(v) => setOverride(o => ({ ...o, action: v }))}
                    options={ACTION_OPTIONS}
                    testId="quick-capture-override-action"
                  />
                  <Select
                    label="Assigned to"
                    value={effective.person || ""}
                    onChange={(v) => setOverride(o => ({ ...o, person: v || null }))}
                    options={[{ id: "", label: "Unassigned" }, ...((members || []).map(m => ({ id: m.id, label: m.name })))]}
                    testId="quick-capture-override-person"
                  />
                  <label className="block">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Due date</span>
                    <input type="date" value={effective.date || ""} onChange={(e) => setOverride(o => ({ ...o, date: e.target.value || null }))} className="mt-1 w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs font-medium text-gray-800 dark:text-gray-100" data-testid="quick-capture-override-date" />
                  </label>
                  {effective.module === "supplies" && (
                    <Select
                      label="Supply category"
                      value={effective.supplyCategory || "Household"}
                      onChange={(v) => setOverride(o => ({ ...o, supplyCategory: v }))}
                      options={SUPPLY_CATEGORIES.map(c => ({ id: c.id, label: c.label }))}
                      testId="quick-capture-override-supply-cat"
                    />
                  )}
                  <label className="block col-span-2">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Item name</span>
                    <input value={effective.item || ""} onChange={(e) => setOverride(o => ({ ...o, itemName: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs font-medium text-gray-800 dark:text-gray-100" data-testid="quick-capture-override-item" />
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm px-6 py-4 border-t border-gray-100 dark:border-slate-800 flex items-center gap-2">
          <button onClick={onClose} className="inline-flex items-center gap-1 px-4 py-2.5 rounded-2xl text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800" data-testid="quick-capture-cancel">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="ml-auto inline-flex items-center gap-1.5    hover: hover: text-white font-bold text-sm px-5 py-2.5 rounded-2xl shadow-md hover:shadow-none active:scale-[0.97] disabled:opacity-50"
            data-testid="quick-capture-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
            Save to Home OS
          </button>
        </div>

        {/* Hidden inputs */}
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} className="hidden" data-testid="quick-capture-camera-input" />
        <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.png,.jpg,.jpeg,.heic" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} className="hidden" data-testid="quick-capture-file-input" />
      </div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/70 dark:bg-slate-800/70 border border-white/40 dark:border-slate-700 text-[10px] font-extrabold text-gray-700 dark:text-gray-200">
      {children}
    </span>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
      <div className="relative">
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full appearance-none rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 pl-2 pr-7 py-1.5 text-xs font-bold text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-300"
          data-testid={testId}
        >
          {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 w-3 h-3 text-gray-400" strokeWidth={1.5} />
      </div>
    </label>
  );
}

export default QuickCaptureModal;
